<template>
  <div class="w-full p-6 bg-white rounded-lg shadow">
    <h4 class="text-base font-semibold">{{ title }}</h4>
    <p class="text-sm text-grey">{{ description }}</p>
    <div class="mt-4">
      <button class="sk-btn sk-btn-primary btn-border rounded-lg" @click="handleCheck">
        {{ buttonLabel }}
      </button>
    </div>
    <SKModal ref="checkDataModal" :title="modalTitle" closeButtonKey="apps.modals.cancel" actionButtonsPosition="center">
      <template #body>
        <div class="min-w-[320px]">
          <div v-if="modalWarnings.length" class="mb-4">
            <div v-for="(warning, index) in modalWarnings" :key="index" class="text-danger text-sm">
              {{ warning }}
            </div>
          </div>

          <div v-for="(section, index) in modalSections" :key="index" class="mb-4">
            <h5 class="text-sm font-semibold mb-2">{{ section.title }}</h5>
            <div v-if="section.rows.length === 0" class="text-sm text-grey">{{ $t('widgets.modal.no_data') }}</div>
            <div
              v-for="(row, rowIndex) in section.rows"
              :key="rowIndex"
              class="flex items-center justify-between py-1"
            >
              <div class="flex items-center gap-2">
                <img v-if="row.icon" :src="row.icon" class="w-5 h-5" />
                <span class="text-sm">{{ row.label }}</span>
              </div>
              <span class="text-sm font-medium">{{ row.value }}</span>
            </div>
          </div>

          <div v-if="modalNotes.length" class="text-xs text-grey">
            <div v-for="(note, index) in modalNotes" :key="index">{{ note }}</div>
          </div>
        </div>
      </template>
    </SKModal>
  </div>
</template>

<script>
import loader from '@/modules/Shared/Mixins/loader'
import SKModal from '@/modules/Shared/Components/modal/SKModal.vue'
import fetchFacilitiesMixin from './fetchFacilitiesMixin'

export default {
  name: 'CheckSimpleTotalsPanel',
  mixins: [loader, fetchFacilitiesMixin],
  components: {
    SKModal
  },
  props: {
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    buttonLabel: {
      type: String,
      required: true
    },
    panelType: {
      type: String,
      required: true
    },
    selectedResortUuid: {
      type: String,
      required: true
    },
    resortInfo: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      modalTitle: '',
      modalWarnings: [],
      modalNotes: [],
      modalSections: []
    }
  },
  methods: {
    setModalNotes(notes) {
      this.modalNotes = notes
    },
    openModal(title, warnings, sections) {
      this.modalTitle = title
      this.modalWarnings = warnings
      this.modalSections = sections
      if (!this.modalNotes) {
        this.modalNotes = []
      }
      this.$refs.checkDataModal.open()
    },
    async handleCheck() {
      await this.checkFacilitiesData()
    },
    async checkFacilitiesData() {
      const type = this.panelType
      const success = await this.loadAndIgnoreError(async () => {
        const facilities = await this.fetchFacilities(type)
        const warnings = []
        const updatedAt = await this.getSnowReportUpdatedAt()

        if (!facilities.length) {
          warnings.push(this.$t('widgets.modal.warning.no_data'))
        }
        if (this.isOutdated(updatedAt, 14)) {
          warnings.push(this.$t('widgets.modal.warning.outdated_14'))
        }

        let totals
        this.setModalNotes([])
        if (type === 'parking') {
          totals = this.buildSimpleTotals(facilities, this.$t('widgets.modal.labels.parkings'), 'parking')
        } else if (type === 'road') {
          totals = this.buildSimpleTotals(facilities, this.$t('widgets.modal.labels.roads'), 'road')
        } else {
          totals = this.buildSimpleTotals(facilities, this.$t('widgets.modal.labels.links'))
        }

        this.setModalNotes(totals.notes)
        const sections = [
          {
            title: this.$t('widgets.modal.facilities.open_vs_total'),
            rows: totals.rows
          }
        ]

        this.openModal(this.facilityTitle(type), warnings, sections)
      })

      if (!success) {
        this.notifyError(this.$t('shops.error.0'), this.$t('widgets.notifications.fetch_facilities_error'))
      }
    },
    buildSimpleTotals(facilities, label, type) {
      let openCount = 0
      let notes = []
      facilities.forEach(facility => {
        if (type === 'parking') {
          if (['free', 'full'].includes(String(facility.parking_state).toLowerCase())) {
            openCount += 1
          }
        } else if (type === 'road') {
          const roadState = String(facility.road_state || '').toLowerCase()
          if (roadState === 'open' || roadState === 'equipment') {
            openCount += 1
          }
          if (roadState === 'equipment') {
            notes = [...notes, this.$t('widgets.modal.notes.road_equipment')]
          }
        } else {
          if (this.isFacilityOpen(facility)) {
            openCount += 1
          }
        }
      })

      const rows = facilities.length > 0 ? [{ label, value: `${openCount} / ${facilities.length}` }] : []
      return { rows, notes }
    },
    isFacilityOpen(facility) {
      const state = (facility.state || facility.status || '').toString().toLowerCase()
      return state.includes('open') || state.includes('in preparation') || state.includes('in-preparation')
    },
    facilityTitle(type) {
      switch (type) {
        case 'link':
          return this.$t('widgets.modal.facilities.links')
        case 'parking':
          return this.$t('widgets.modal.facilities.parking')
        case 'road':
          return this.$t('widgets.modal.facilities.access')
        default:
          return this.$t('widgets.modal.facilities.default')
      }
    }
  }
}
</script>

